import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { VersionInfo } from '../../components/VersionInfo';
import { MobileHeader } from '../../components/admin/MobileHeader';
import {
  LayoutDashboard,
  Users,
  Globe,
  MailOpen,
  Send,
  Layers,
  ScrollText,
  FlaskConical,
  BarChart3,
  Image,
  X,
  type LucideIcon,
} from 'lucide-react';

const navItems: Array<{ path: string; label: string; icon: LucideIcon }> = [
  { path: '/admin', label: 'Overview', icon: LayoutDashboard },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/worlds', label: 'Worlds', icon: Globe },
  { path: '/admin/letters-in', label: 'Inbound Mail', icon: MailOpen },
  { path: '/admin/letters-out', label: 'Outbound Mail', icon: Send },
  { path: '/admin/thumbnails', label: 'Thumbnails', icon: Image },
  { path: '/admin/queues', label: 'Queues', icon: Layers },
  { path: '/admin/analytics', label: 'LLM Analytics', icon: BarChart3 },
  { path: '/admin/qa', label: 'QA Testing', icon: FlaskConical },
  { path: '/admin/audit', label: 'Audit Log', icon: ScrollText },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Close sidebar on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isSidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-void flex">
      {/* Mobile Header */}
      <MobileHeader
        onMenuClick={() => setIsSidebarOpen(true)}
        title="Loremail Admin"
        userEmail={user?.email}
      />

      {/* Backdrop overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:static w-64 h-screen bg-night border-r border-stone/30 flex flex-col z-50 md:z-auto transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        {/* Header */}
        <div className="p-4 border-b border-stone/30 relative">
          <button
            onClick={closeSidebar}
            className="md:hidden absolute top-4 right-4 p-1.5 text-mist hover:text-parchment hover:bg-stone/30 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
          <h1 className="font-display text-lg text-parchment">Loremail Admin</h1>
          <p className="text-mist/70 text-xs mt-1">{user?.email}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/admin'}
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 sm:py-3 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-rune/20 text-rune border border-rune/30'
                          : 'text-mist hover:bg-stone/30 hover:text-parchment'
                      }`
                    }
                  >
                    <Icon size={18} className="opacity-70" />
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-stone/30 space-y-2">
          <button
            onClick={handleBackToDashboard}
            className="w-full px-3 py-3 text-sm text-mist hover:text-parchment hover:bg-stone/30 rounded-lg transition-colors text-left"
          >
            Back to Dashboard
          </button>
          <button
            onClick={handleLogout}
            className="w-full px-3 py-3 text-sm text-mist hover:text-ember hover:bg-ember/10 rounded-lg transition-colors text-left"
          >
            Logout
          </button>
          <div className="pt-2 text-center">
            <VersionInfo />
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto pt-14 md:pt-0">
        <div className="p-4 sm:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
